import styled from '@emotion/styled';
import {
  CircularProgress,
  Dialog,
  IconButton,
  Menu,
  MenuItem
} from '@material-ui/core';
import {
  MoreHorizontal as IconMoreHori,
  MoreVertical as IconMoreVert
} from 'lucide-react';
import React, { createElement, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useId } from '../../hooks/useId';
import { LinkExternal } from '../LinkExternal';

type DialogComponent = React.ComponentType<{ onClose: () => void }>;

const Loader = styled(CircularProgress)`
  width: 20px !important;
  height: 20px !important;

  svg {
    color: inherit;
  }
`;

export interface AdditionActionsMenuOption {
  key: string;
  label: React.ReactNode;
  onClick?: () => any;
  href?: string;
  externalHref?: string;
  dialog?: DialogComponent;
  dialogMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  dialogFullWidth?: boolean;
  icon?: React.ComponentType<{
    size?: string | number;
    style?: React.CSSProperties;
  }>;
  divider?: boolean;
  disabled?: boolean;
}

export type AdditionalActionsMenuProps = {
  label?: string;
  options: AdditionActionsMenuOption[];
  variant?: 'vertical' | 'horizontal';
};

const OptionContainer = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const OptionLabel = ({ o }: { o: AdditionActionsMenuOption }) => (
  <OptionContainer>
    {o.icon && createElement(o.icon, { size: 16, style: { marginRight: 8 } })}
    {o.label}
  </OptionContainer>
);

const ICON_SIZE = 20;

export const AdditionalActionsMenu = ({
  options,
  label = '',
  variant = 'vertical'
}: AdditionalActionsMenuProps) => {
  const menuId = useId();
  const [dialogState, setDialogState] = useState<{
    comp: DialogComponent;
    maxWidth?: AdditionActionsMenuOption['dialogMaxWidth'];
    fullWidth?: AdditionActionsMenuOption['dialogFullWidth'];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement | null>(null);

  const close = (ev: any) => {
    // need to resort to any, because the type of MUI's Menu#onClose is wrong
    ev.stopPropagation();
    ev.preventDefault();
    setOpen(false);
  };
  const closeDialog = () => setDialogState(null);

  if (!options.length) {
    return null;
  }

  return (
    <>
      <IconButton
        ref={ref}
        aria-label={label}
        aria-owns={open ? menuId : undefined}
        aria-haspopup="true"
        onClick={(ev) => {
          ev.stopPropagation();
          ev.preventDefault();
          return setOpen((o) => !o);
        }}
      >
        {loading ? (
          <Loader />
        ) : variant === 'vertical' ? (
          <IconMoreVert size={ICON_SIZE} />
        ) : (
          <IconMoreHori size={ICON_SIZE} />
        )}
      </IconButton>

      <Menu
        id={menuId}
        open={open}
        onClose={close}
        anchorEl={ref.current}
        onClick={(ev) => ev.stopPropagation()}
      >
        {options.map((option) => {
          const { onClick, href, externalHref, dialog } = option;
          if (onClick) {
            return (
              <MenuItem
                key={option.key}
                onClick={(ev) => {
                  ev.stopPropagation();
                  ev.preventDefault();
                  const res = onClick();
                  if (res && res.then) {
                    setLoading(true);
                    res.then(() => {
                      setLoading(false);
                    });
                  }
                  setOpen(false);
                }}
                divider={option.divider}
                disabled={option.disabled}
              >
                <OptionLabel o={option} />
              </MenuItem>
            );
          }
          if (href) {
            return (
              <Link to={href} key={option.key}>
                <MenuItem divider={option.divider} disabled={option.disabled}>
                  <OptionLabel o={option} />
                </MenuItem>
              </Link>
            );
          }
          if (externalHref) {
            return (
              <LinkExternal href={externalHref} key={option.key}>
                <MenuItem divider={option.divider} disabled={option.disabled}>
                  <OptionLabel o={option} />
                </MenuItem>
              </LinkExternal>
            );
          }

          if (dialog) {
            return (
              <MenuItem
                key={option.key}
                onClick={(ev) => {
                  ev.stopPropagation();
                  ev.preventDefault();
                  setOpen(false);
                  setDialogState(() => ({
                    comp: dialog,
                    maxWidth: option.dialogMaxWidth,
                    fullWidth: option.dialogFullWidth
                  }));
                }}
                divider={option.divider}
                disabled={option.disabled}
              >
                <OptionLabel o={option} />
              </MenuItem>
            );
          }
          return null;
        })}
      </Menu>
      <Dialog
        open={!!dialogState}
        onClose={closeDialog}
        maxWidth={dialogState?.maxWidth}
        fullWidth={dialogState?.fullWidth}
      >
        {dialogState ? (
          createElement(dialogState.comp, { onClose: closeDialog })
        ) : (
          <div />
        )}
      </Dialog>
    </>
  );
};
